import { MRT_ColumnDef, MRT_RowData } from "material-react-table";
import { useEffect, useMemo, useRef } from "react";
import { TagContextOptions, useTagFiltersData } from "src/components/Filter/TagFilter/useTagFiltersData";
import { ColumnsService } from "src/utils/ColumnsService";
import { generateTagColumns, getDefaultTagColumnVisibility, type TagColumnAppearance } from "src/utils/tagColumns";

type TagColumns<Data extends MRT_RowData> = MRT_ColumnDef<Data, any>;

type Options = {
    appearance?: TagColumnAppearance;
    gridKey?: string;
    context: TagContextOptions;
};

type TagColumnsReturn<Data extends MRT_RowData> = {
    tagColumns: TagColumns<Data>[] | undefined;
};
/**
 *
 * @param options
 * @returns Columns that can be used inside MRT. Don't forget to **CAST** the type
 *
 * @example
 * useTagsColumn<YourType>({ context: "global" })
 */
export const useTagsColumn = <Data extends MRT_RowData>(options: Options): TagColumnsReturn<Data> => {
    const { appearance = "legacy", context, gridKey } = options;

    const { tagTypes, tagValues, isReady } = useTagFiltersData({ context });

    const tagTypesSignature = useMemo(() => tagTypes.map((tagType) => tagType.slug).join("|"), [tagTypes]);
    const initializedVisibilitySignatureRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isReady || gridKey === undefined || tagTypesSignature === "") {
            return;
        }

        const initSignature = `${gridKey}::${tagTypesSignature}`;
        if (initializedVisibilitySignatureRef.current === initSignature) {
            return;
        }

        initializedVisibilitySignatureRef.current = initSignature;
        const defaultTagVisibility = getDefaultTagColumnVisibility(tagTypes);
        ColumnsService.initializeTagColumnsVisibility(gridKey, defaultTagVisibility);
    }, [gridKey, isReady, tagTypes, tagTypesSignature]);

    const tagColumns = useMemo(() => {
        if (!isReady) {
            return undefined;
        }

        return generateTagColumns<Data>({
            appearance,
            tagTypes,
            tagValues,
            readonly: true,
        });
    }, [appearance, isReady, tagTypes, tagValues]);

    return { tagColumns };
};
